import { reachGoal, reportToSentry } from '@/util';
import { createFetchHttpClient } from './client';
import {
    isAppWebView,
    isLoggedIn,
    requestSignIn,
    sessionHeaders,
} from './eligibility';
import {
    AI_FIELD_SELECTOR,
    askTrialGate,
    rememberTrialArm,
    TRIAL_ARM,
} from './trial';
import {
    fieldAllowsTrigger,
    fieldOffersLengthChoice,
    insertGeneratedText,
} from './dom';
import { failureTags, messageForFailure, TFailureReason } from './failure';

// Standalone bootstrap (rule #18 / §13): attach an "✨ generate" trigger next to
// every opted-in card text field (content textarea OR caption input) and open the
// react-ui drawer on click. The reactUI global comes from react-ui.js (loaded
// site-wide in the footer); this lib only runs where a `data-ai-greeting` field
// exists, so it self-noops elsewhere.

type TTextField = HTMLTextAreaElement | HTMLInputElement;

const MOUNTED_FLAG = 'aiGreetingMounted';
// The music caption field (name="caption") is the short, prose-only field; the card
// body is name="content". See isCaption().
const CAPTION_FIELD_NAME = 'caption';
// Yandex Metrika: trigger opened the generator (funnel: doc/ai/metrika-goals.md)
const GOAL_INIT = 'ai-greeting-init';
// on GOAL_INIT when the trial gate gave no answer (doc/ai/metrika-goals.md)
const GOAL_PARAMS_GATE_FAILED = { gate: 'failed' };
// the trigger while a visitor not signed in waits for the trial gate
const LOADING_CLASS = 'is-loading';

// The caption field is short, prose-only: it skips the length AND format selectors
// and always generates prose (a 2-line wish reads well even at caption length). It
// still goes through the same min-length trigger guard as any other field — a
// caption capped under MIN_FIELD_MAXLENGTH is too short for AI text to help.
// Keyed off the field's `name` (semantic) rather than a marker attribute.
function isCaption(field: TTextField): boolean {
    return field.name === CAPTION_FIELD_NAME;
}

// The applied ping sends the slug; the server resolves card_id from it.
function cardSlugFor(field: TTextField): string | undefined {
    const input = field.form?.querySelector<HTMLInputElement>('input[name="slug"]');
    return input?.value || undefined;
}

// Дровер не открылся: кнопка на вид сработала, а не произошло ничего.
//
// Портал со спиннером reactUI убирает за собой (react-ui/index.ts), поэтому
// молчаливая ветка здесь — это мёртвый клик, а отклонение промиса без своего
// обработчика уехало бы в Sentry глобальным unhandledrejection: без section,
// без причины и без связи с этой кнопкой.
function failOpen(reason: TFailureReason, cause?: unknown): void {
    reportToSentry({
        section: 'ai_text_drawer',
        level: 'error',
        error: cause ?? new Error(`ai text drawer did not open: ${reason}`),
        extras: { drawer_failure: `ai text drawer did not open: ${reason}` },
        tags: failureTags(reason, cause),
    });
    alert(messageForFailure(reason));
}

function openDrawer(
    field: TTextField,
    anonymousTrial: boolean,
    trialSpent = false
): void {
    // Не `typeof reactUI === 'undefined'`: глобал может СУЩЕСТВОВАТЬ без
    // вызываемого show() — частично загрузившийся или старый react-ui.js. Тогда
    // прямой вызов бросал бы TypeError синхронно, прямо в обработчике клика:
    // мёртвый клик плюс голая необработанная ошибка, то есть ровно то, что этот
    // модуль и закрывает. `typeof reactUI` первым операндом обязателен — на
    // необъявленном имени падает даже `typeof reactUI?.show`.
    if (typeof reactUI === 'undefined' || typeof reactUI.show !== 'function') {
        failOpen('react_ui_missing');
        return;
    }
    const caption = isCaption(field);
    try {
        // The feature is live on web + app for any logged-in user, and for a visitor
        // in the anonymous-trial arm; the backend re-checks both (this is a UX hint).
        // Otherwise → sign-in CTA (web auth popup, or the app's native sign-in via
        // the bridge — both behind requestSignIn).
        reactUI
            .show('greeting-text-generator', {
                eligible: anonymousTrial || isLoggedIn(),
                anonymousTrial,
                trialSpent,
                maxLength: field.maxLength > 0 ? field.maxLength : undefined,
                showLength: !caption && fieldOffersLengthChoice(field.maxLength),
                // captions: no стихи — only проза/пожелание, default проза
                allowedFormats: caption ? ['prose', 'wish'] : undefined,
                initialFormat: caption ? 'prose' : 'poem',
                // web → site auth popup; in-app → app sign-in via bridge
                onAuth: requestSignIn,
                // optional per-form override of the web "download the app" upsell link
                storeUrl: field.dataset.aiAppStoreUrl || undefined,
                // getter, not a value: a fresh card may only get its slug after the open
                getCardSlug: () => cardSlugFor(field),
                onInsert: (text: string) => insertGeneratedText(field, text),
            })
            .catch((e) => failOpen('drawer_show_rejected', e));
    } catch (e) {
        // Симметрия с PayHelper.coffee: там синхронный бросок ловит try/catch
        // вокруг того же вызова. Две кассы — один словарь причин.
        failOpen('drawer_show_threw', e);
    }
}

// A visitor not signed in first asks the trial gate (doc/ai/greeting-text-anonymous-
// trial.md): the arm decides between the generator and the sign-in offer, and rides
// on GOAL_INIT so the funnel splits by arm before a card exists.
async function onTriggerClick(
    field: TTextField,
    button: HTMLButtonElement
): Promise<void> {
    // Outside the trial, the sign-in offer as before: the app; and an existing
    // card – the cohort is cards created after the gate, and the card form
    // restores the text through the sign-in reload only on a new card.
    if (isLoggedIn() || isAppWebView() || cardSlugFor(field)) {
        reachGoal(GOAL_INIT);
        openDrawer(field, false);
        return;
    }

    if (button.disabled) return;
    button.disabled = true;
    button.classList.add(LOADING_CLASS);
    // never rejects: a failed gate is '' – the sign-in offer, as before the test
    const { arm, spent, failed } = await askTrialGate(
        createFetchHttpClient(sessionHeaders)
    );
    button.disabled = false;
    button.classList.remove(LOADING_CLASS);

    rememberTrialArm(field.form, arm);
    reachGoal(
        GOAL_INIT,
        arm ? { arm } : failed ? GOAL_PARAMS_GATE_FAILED : undefined
    );
    openDrawer(field, arm === TRIAL_ARM, spent);
}

function attach(field: TTextField): void {
    if (field.dataset[MOUNTED_FLAG]) return;
    // Skip fields too short for a worthwhile greeting — a cramped AI couplet (or
    // a truncated AI wish) reads worse than none.
    if (!fieldAllowsTrigger(field.maxLength)) return;
    field.dataset[MOUNTED_FLAG] = '1';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ai-greeting-trigger';
    button.innerHTML = '✨ <span>Сгенерировать текст в ИИ</span>';
    button.addEventListener('click', () => {
        void onTriggerClick(field, button);
    });

    const anchor = field.closest('.input-wrapper') ?? field;
    anchor.insertAdjacentElement('afterend', button);
}

// One-shot scan is sufficient: the trigger ships inside the same server-rendered
// cards/form.html.twig as the textarea, so the fields exist by the time this runs.
// (No MutationObserver/delegation like recent-card-bar — card forms aren't injected
// post-load here. Revisit if a card form ever becomes client-rendered.)
function init(): void {
    const fields = document.querySelectorAll<TTextField>(AI_FIELD_SELECTOR);
    if (fields.length === 0) return;
    // `.ai-greeting-trigger` is styled in assets/stylus/parts/ai-greeting.styl.
    fields.forEach(attach);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
